import { useRef, useState } from 'react';
import type { BotDifficulty } from '@cribbage/bots';
import { kindLook } from '~/session/faceOrder.js';
import { useOwnFace } from '~/store/authStore.js';
import { useSettingsStore } from '~/store/settingsStore.js';
import { AvatarRig, type Gaze, type Mood, type Motion, type Phase } from './AvatarRig.js';
import { BOT_RIGS } from './botFaces.js';
import { personRig } from './castFaces.js';
import { cx } from './cx.js';
import { drawnStill, stillWanted } from './reducedMotion.js';
import { useBrowserLayoutEffect } from './useBrowserLayoutEffect.js';
import {
  SeatDrawing,
  plateRadius,
  type SeatFace,
  type SeatSide,
  type SeatTone,
} from '~/table/cross/faces.js';

/**
 * One seat's mark: a catalog face inside a ring that carries the seat's side
 * and whether the table is waiting on them.
 *
 * Drawn at four sizes across the Cross screens — 56 in the phone band, 44 in
 * the laptop scoreboard, 40 in a count tally, 88 in a payout well — and it is
 * one component at all of them, so a player wears one mark wherever they
 * appear.
 *
 * **The turn is spent on the ring and on the wash around it, never on a
 * fill.** `brain/brand/BRAND.md` colour rule 4 bans a solid team field
 * outright, so an active seat draws its ring at the side's bare stop and a
 * resting seat drops to `-quiet`. That is the same pair of strengths the lane
 * marks use, one row up, and for the same reason: the ring still says which
 * side this player is, and its strength says whether the table is waiting on
 * them. It is never a low opacity, which would punch a hole in the mark and
 * drag the table through it.
 *
 * **The wash is the caller's, and `seatWash` is the rule.** Which stop a wash
 * takes is decided by what is behind it (BRAND § Teal and tangerine): `-quiet`
 * on the cream table, `-muted` on a white panel. A seat sits on both, so the
 * stop cannot be baked in here.
 *
 * A human seat wears a catalog face (`faces.tsx`). A `you` head drawn with no
 * table to ask (Settings, Stats, Home) wears the face the player chose, and
 * follows it when they change it. At game over the face comes alive for the
 * result: the catalog face's rig (`ui/castFaces.ts`) plays the win or the
 * loss and holds it. Patch alone keeps the still, because his parrot is a
 * puppet only the sketch draws.
 *
 * **A bot seat whose level is known wears that level's house bot**, the tin
 * robot `ui/botFaces.ts` rigs, animated by `AvatarRig` on the same stone plate
 * the plain bot mark stands on. On the table and in the game-over well it
 * breathes, blinks and looks around; on its own turn it looks down at its
 * cards; at game over it wears the win or the loss face. The 20 chip never
 * moves, off-table callers pass `still`, and reduced motion stills every copy.
 * A bot with no known level (one covering a person who left, or a game that
 * never recorded one) keeps the plain mark.
 *
 * It lives in `ui/` because Classic's table, the Resume door, Home, Stats and
 * Settings wear it as well as Cross. The drawings stay in
 * `table/cross/faces.tsx` beside the table's seat model (`CrossSeat`,
 * `SeatSide`, `SeatCrib`), which two dozen table files read; this is the only
 * file that calls them.
 */

/**
 * The head's face (`kind`, `look`, `botLevel`) is a `SeatFace`: call sites
 * spread `seatFace(seat)`, which drops a level off any seat that is not a bot.
 */
export interface AvatarProps extends SeatFace {
  /**
   * The result face: `win` and `loss` are the game-over wells' (`resultMood`).
   * A house bot wears its robot's; a person wears their catalog face's rig,
   * and at rest the still.
   */
  readonly mood?: Mood | undefined;
  /** Where the house bot looks: `down` on its own turn (`turnGaze`), `idle` otherwise. */
  readonly gaze?: Gaze | undefined;
  /** Draw the house bot without motion: every head off the table. The chip is always still. */
  readonly still?: boolean | undefined;
  /**
   * Which way the face turns. Every face is drawn three-quarter left, so
   * `right` mirrors it. Two sides drawn side by side face each other
   * (`inwardFacing`); a head on its own keeps the drawing's left.
   */
  readonly facing?: SeatFacing | undefined;
  /** Which lines this seat scores: rows are teal, columns tangerine. */
  readonly side: SeatSide;
  /** Which place on the table this head is standing in. */
  readonly size: keyof typeof SIZE;
  /**
   * A corner or a circle.
   *
   * Squared, so that round means tappable: the board cells and the cards are
   * rounded rectangles, and a squared head is the table's own corner language.
   * The corner is about a third of the head at every size, so one player wears
   * one shape on the phone and the laptop alike.
   */
  readonly shape?: 'square' | 'round';
  /** The table is waiting on this seat. */
  readonly active?: boolean;
  /**
   * How the ring is drawn.
   *
   * `ink` is a 2px ink stroke with the side's colour as a 3px halo outside it:
   * what a head inside a white panel wears, which can hold the weight. `side`
   * drops the ink and lets the side colour be the whole ring: what the phone
   * band wears, where a head carrying 2 points of ink and 3 more of colour at
   * 56 points swamps everything beside it. `plain` is the ink stroke alone,
   * with no side at all: off the table a head has no side yet, and a teal
   * halo on the home screen's lemon Resume door reads 1.10:1 against it.
   */
  readonly outline?: 'ink' | 'side' | 'plain';
  /**
   * A seat color the two sides do not have: `periwinkle`, Classic's third
   * player. It takes the ring and a person's plate from the side, at the same
   * stops (bright on move, `-quiet` resting) and in the same two outlines.
   * Unset everywhere in Cross, which draws exactly what it drew before.
   */
  readonly tone?: SeatTone | undefined;
  /**
   * The seat's name, which is this mark's alternative text.
   *
   * Omit it where the enclosing station already names the seat: two names for
   * one seat is the legend mistake one row down. A mark with no name is hidden
   * from the accessibility tree outright rather than left as an unlabelled
   * image.
   */
  readonly name?: string | undefined;
  /** Layout only. Never a colour, a radius or a border. */
  readonly className?: string | undefined;
}

/** The corner, as a share of the head. 19 on 56 in the band, 15 on 44 in the
    laptop panel: the same ratio at both sizes, which is what keeps one player
    wearing one shape on both screens. */
const SIZE = {
  /** In a line of text: the count ledger, a hand-over row, the start panel. */
  chip: { px: 20, box: 'size-5', corner: 'rounded-lg', radius: 8 },
  /** The laptop seat panel. */
  panel: { px: 40, box: 'size-10', corner: 'rounded-xl', radius: 12 },
  /** Beside a station's name on the scoreboard. */
  station: { px: 44, box: 'size-11', corner: 'rounded-2xl', radius: 16 },
  /** The phone score band. */
  band: { px: 56, box: 'size-14', corner: 'rounded-2xl', radius: 16 },
  /** The seat well, and the game-over screen. */
  hero: { px: 88, box: 'size-22', corner: 'rounded-4xl', radius: 32 },
  /** The Classic phone header with three seats, a face over each score. */
  band3: { px: 48, box: 'size-12', corner: 'rounded-2xl', radius: 16 },
  /** The same header with four, two faces over each team's score. */
  band4: { px: 40, box: 'size-10', corner: 'rounded-2xl', radius: 16 },
  /** A partner's head inside a team's row on the Classic laptop panel. */
  member: { px: 32, box: 'size-8', corner: 'rounded-xl', radius: 12 },
  /**
   * The Classic pass-and-play cover and its sheet, phone then laptop: the one
   * face on a screen that calls its seat. The corner is a third of the head,
   * the sketch's rule, which no radius step carries, so it is `classic.css`'s
   * `classic-handoff-head` rather than a stock class.
   */
  sheet: { px: 64, box: 'size-16', corner: 'classic-handoff-head', radius: 21 },
  sheetWide: { px: 72, box: 'size-18', corner: 'classic-handoff-head', radius: 24 },
  cover: { px: 112, box: 'size-28', corner: 'classic-handoff-head', radius: 37 },
  coverWide: { px: 132, box: 'size-33', corner: 'classic-handoff-head', radius: 44 },
} as const;

/**
 * The ring, per side and per state. Four complete literals, because Tailwind
 * reads source text: a class assembled from a variable emits nothing.
 *
 * `ring-3` sits outside the 2px border with no offset, which is exactly the
 * `0 0 0 3px` halo the sketch drew as a box-shadow.
 */
const HALO = {
  rows: { on: 'border-ink ring-3 ring-teal', off: 'border-ink ring-3 ring-teal-quiet' },
  columns: {
    on: 'border-ink ring-3 ring-tangerine',
    off: 'border-ink ring-3 ring-tangerine-quiet',
  },
} as const;

/**
 * How the house bots move in the game. Settle, because BRAND.md says an
 * interaction settles and never bounces; the playful set auditions BRAND open
 * question 4 on the Shared - Avatars sketch. Flip it here and every seat follows.
 */
export const SEAT_MOTION: Motion = 'settle';

/**
 * Where each level starts its breath and its sway, so two bots of different
 * levels at one table never move in step.
 */
const BOT_PHASE: Record<BotDifficulty, Phase> = { easy: 0, medium: 1, hard: 2, expert: 3 };

/**
 * A person's plate under their rig, the same stop the still's plate takes
 * (`faces.tsx`). Complete literals, so a renamed stop is one grep.
 */
const RIG_PLATE: Record<SeatSide, string> = {
  rows: 'bg-teal-quiet',
  columns: 'bg-tangerine-quiet',
};
const RIG_TONE_PLATE: Record<SeatTone, string> = { periwinkle: 'bg-periwinkle-quiet' };

/** The two ways a seat's face turns. The rig's `front` is for a turn only. */
export type SeatFacing = 'left' | 'right';

/**
 * Two sides side by side face each other: the left one turns right, toward
 * the middle. A row of three or more keeps the drawing's left.
 */
export function inwardFacing(index: number, count: number): SeatFacing {
  return count === 2 && index === 0 ? 'right' : 'left';
}

/** A seat's gaze from the turn: a bot on move looks down at its cards. */
export function turnGaze(onMove: boolean): Gaze {
  return onMove ? 'down' : 'idle';
}

/** A finished game's face: the winner laughs and every other side takes the loss. */
export function resultMood(won: boolean): Mood {
  return won ? 'win' : 'loss';
}

/**
 * A house bot stands still inside a still table (`data-motion="none"`: the
 * motion setting, fast-forward, a `motion=none` stage) as well as under the
 * operating system's preference. The table says so on an ancestor, not in a
 * prop every seat would have to thread, so the head reads it from the DOM
 * before the first paint, as the grid and the strip do (`drawnStill`).
 */
function useTableStill(drawing: boolean): {
  readonly ref: React.RefObject<HTMLDivElement | null>;
  readonly still: boolean;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [still, setStill] = useState(false);
  useBrowserLayoutEffect(() => {
    const node = ref.current;
    setStill(drawing && node !== null && drawnStill(node));
  });
  return { ref, still };
}

const BARE = {
  rows: { on: 'border-teal', off: 'border-teal-quiet' },
  columns: { on: 'border-tangerine', off: 'border-tangerine-quiet' },
} as const;

/** A toned seat's two rings, the same complete literals per state. */
const TONED = {
  periwinkle: {
    ink: {
      on: 'border-ink ring-3 ring-periwinkle',
      off: 'border-ink ring-3 ring-periwinkle-quiet',
    },
    side: { on: 'border-periwinkle', off: 'border-periwinkle-quiet' },
  },
} satisfies Record<SeatTone, Record<'ink' | 'side', { on: string; off: string }>>;

export function Avatar({
  kind,
  side,
  size,
  shape = 'square',
  active = false,
  outline = 'ink',
  look,
  botLevel,
  mood = 'rest',
  gaze = 'idle',
  still = false,
  facing = 'left',
  tone,
  name,
  className,
}: AvatarProps): React.JSX.Element {
  const reducedMotion = useSettingsStore((state) => state.reducedMotion);
  const own = useOwnFace();
  const robot = kind === 'bot' && botLevel !== undefined;
  /* A person's face moves only for a result: the winner's face or the
     loser's, where game over reads the match off the people. Everywhere
     else the still stands, as it always has. */
  const personLook =
    kind === 'you' || kind === 'person' ? (look ?? kindLook(kind, own)) : undefined;
  const personal = mood !== 'rest' && personLook !== undefined ? personRig(personLook) : undefined;
  const table = useTableStill(robot || personal !== undefined);
  const rigStill = still || size === 'chip' || table.still || stillWanted(reducedMotion);
  const scale = SIZE[size];
  const radius = shape === 'round' ? undefined : scale.radius;
  const state = active ? 'on' : 'off';
  const ring =
    outline === 'plain'
      ? 'border-ink'
      : tone !== undefined
        ? TONED[tone][outline][state]
        : outline === 'ink'
          ? HALO[side][state]
          : BARE[side][state];
  const named = name !== undefined && name !== '';

  return (
    <div
      ref={table.ref}
      className={cx(
        'cross-avatar flex flex-none items-center justify-center overflow-hidden border-2 bg-ground-light',
        ring,
        scale.box,
        shape === 'round' ? 'rounded-full' : scale.corner,
        className,
      )}

      {...(named ? { role: 'img', 'aria-label': name } : { 'aria-hidden': true })}
    >
      {robot ? (
        <AvatarRig
          rig={BOT_RIGS[botLevel]}
          mood={mood}
          facing={facing}
          gaze={gaze}
          frame="size-full bg-ground-dark"
          motion={SEAT_MOTION}
          phase={BOT_PHASE[botLevel]}
          still={rigStill}
        />
      ) : personal !== undefined ? (
        <AvatarRig
          rig={personal}
          mood={mood}
          facing={facing}
          gaze={gaze}
          frame={cx('size-full', tone === undefined ? RIG_PLATE[side] : RIG_TONE_PLATE[tone])}
          motion={SEAT_MOTION}
          still={rigStill}
        />
      ) : (
        <SeatDrawing
          kind={kind}
          side={side}
          tone={tone}
          rx={plateRadius(SIZE[size].px, radius)}
          look={look ?? kindLook(kind, own)}
          mirrored={facing === 'right'}
        />
      )}
    </div>
  );
}

/**
 * The wash a seat's row or pill takes, which is a different question from the
 * ring and has a different answer on each ground.
 *
 * BRAND § Teal and tangerine: `-muted` is a real mix into white and dies on
 * the cream table (`--tangerine-muted` lands within about two percent of
 * `--ground` on every channel); `-quiet` is mixed into the ground instead. So
 * the stop is decided by what is behind the wash, not by taste.
 *
 * A resting seat gives its wash up entirely — to the table on cream, to the
 * panel's own white on a panel. That is half of what the turn costs a seat;
 * the ring is the other half. The score numeral is never quieted by either,
 * because `docs/ux/cross/table.md` requires the match so far to stay exactly
 * as readable in any state that disables an action.
 */
export function seatWash(side: SeatSide, active: boolean, ground: 'cream' | 'white'): string {
  if (!active) return ground === 'cream' ? 'bg-transparent' : 'bg-ground-light';
  if (ground === 'cream') return side === 'rows' ? 'bg-teal-quiet' : 'bg-tangerine-quiet';
  return side === 'rows' ? 'bg-teal-muted' : 'bg-tangerine-muted';
}

/** The only stop a team-coloured word may wear. Both literals, both greppable. */
export function seatWord(side: SeatSide): string {
  return side === 'rows'
    ? 'border-teal-text text-teal-text'
    : 'border-tangerine-text text-tangerine-text';
}
